import { useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { loadModule, isLoaded, getModule, getLoadError } from 'holocron';
import runAsync from './runAsync';

/**
 *
 * @param {string} moduleName - name of the module
 * @param {{ defer: boolean }} defer - defer: determines if the async call to
 * retrieve the module is deferred
 * @returns {{ isLoading: boolean, Module: object, error: Error | undefined, run: function }} -
 *  Returns the loaded module from the cache and function to when the async call is deferred.
 */
const useModule = function (moduleName) {
  let {
    defer = false
  } = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  const loaded = useSelector(isLoaded(moduleName));
  const data = loaded ? getModule(moduleName) : undefined;
  const error = useSelector(getLoadError(moduleName));
  const dispatch = useDispatch();
  const runModule = useCallback(() => dispatch(loadModule(moduleName)), [dispatch, moduleName]);
  const run = useCallback(() => runAsync(runModule), [runModule]);
  useEffect(() => {
    if (defer || loaded) {
      return;
    }
    runAsync(runModule());
  }, [runModule, defer, loaded]);
  return useMemo(() => ({
    isLoading: !(loaded || defer),
    data,
    Module: data,
    error,
    run
  }), [defer, loaded, data, error, run]);
};
export default useModule;